import React from 'react';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  maxWidthClassName?: string;
}

/**
 * Shared popup shell used across the app for add/edit dialogs (category,
 * product, and generic multi-record item editing). Mirrors the markup the
 * "Add Section" / "Change Layout" modals in EditWebsitePage.tsx already use,
 * so all popups share the same look and mobile behavior (full-screen sheet
 * on small screens, centered card on larger ones).
 */
const Modal: React.FC<ModalProps> = ({ isOpen, onClose, title, children, maxWidthClassName = 'max-w-2xl' }) => {
  if (!isOpen) return null;

  return (
    <div className="nova-modal-overlay p-0 sm:p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`nova-card shadow-nova-lg ${maxWidthClassName} w-full h-full sm:h-auto max-h-full sm:max-h-[90vh] sm:rounded-2xl rounded-none overflow-hidden flex flex-col`}
      >
        <div className="p-4 sm:p-6 border-b border-nova-border flex-shrink-0">
          <div className="flex justify-between items-center">
            <h3 className="text-xl sm:text-2xl font-bold text-nova-text">{title}</h3>
            <button
              onClick={onClose}
              className="text-nova-text3 hover:text-nova-text2 p-2 -m-2 flex items-center justify-center min-h-[44px] min-w-[44px]"
              aria-label="Close"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-4 sm:p-6 overflow-y-auto flex-1">{children}</div>
      </div>
    </div>
  );
};

export default Modal;
